// Keeps task lists in step with changes made by other users, off the back of the PushEventService
// poll. Like the waitlist, this used to reload and now patches one task, against the single-task GET
// added for it.
//
// Both views that show tasks hold the same two lists, fetched by the same two queries:
//
//   assignedTasks - POST /task/assigned, the tasks assigned to this user
//   practiceTasks - POST /task/query, every task across the user's practices
//                   (the views call this one "createdTasks", but the query is practice-wide)
//
// A task can belong to either, both, or neither, and an edit can move it between them - reassigning
// a task takes it out of the assigned list while leaving it in the practice one. So a patch decides
// membership of each list separately and adds or removes accordingly, rather than assuming the task
// stays where it was found.
//
// Both queries are bounded by a due-date window, so that bound is applied here too: a task due
// outside what the view asked for must not be patched in, or it would appear until the next refresh
// dropped it again.
app.service("TaskLiveUpdateService", [
	"TasksAPI",
	"LiveReloadService",
	"Session",
	"PUSH_EVENTS",
	"LiveUpdateGuard",
	function (TasksAPI, LiveReloadService, Session, PUSH_EVENTS, LiveUpdateGuard) {
		/**
		 * Wires a view up to task push events for the life of its scope.
		 *
		 * @param scope the view's scope; listeners are removed when it is destroyed
		 * @param spec  assignedTasks() - the assigned-to-me list, patched in place
		 *              practiceTasks() - the practice-wide list, patched in place
		 *              window() - {start, end} UTC ISO, the due-date range the view queried
		 *              isRelevant(payload) - whether the change is worth a request for this view
		 *              onChanged() - re-merge, re-count and re-render after a patch
		 */
		function subscribe(scope, spec) {
			// Task ids with a fetch in flight. A task publishes to both the practice channel and the
			// assignee's user channel, so the assignee receives two events - different event ids, so the
			// poll's own de-duplication does not catch them. Without this each would fetch the same task.
			const fetching = {};

			/** A list from the spec, or null when the view has not loaded it yet. */
			function listOf(getter) {
				const list = getter ? getter() : null;
				return angular.isArray(list) ? list : null;
			}

			/** The index of a task in a list, or -1. */
			function indexOf(list, taskId) {
				for (let i = 0; i < list.length; i++) {
					if (LiveReloadService.sameId(list[i].task_id, taskId)) {
						return i;
					}
				}
				return -1;
			}

			/** Drops every copy of a task from a list. Returns whether anything was removed. */
			function remove(list, taskId) {
				let removed = false;
				for (let i = list.length - 1; i >= 0; i--) {
					if (LiveReloadService.sameId(list[i].task_id, taskId)) {
						list.splice(i, 1);
						removed = true;
					}
				}
				return removed;
			}

			/**
			 * Whether the task falls inside the due-date window the view queried. The queries range over
			 * the due date, so that is the field compared - not the created or updated date.
			 */
			function isInWindow(task) {
				const range = spec.window();
				if (!range || !range.start || !range.end || !task.date_due_UTC) {
					return false;
				}
				const due = moment.utc(task.date_due_UTC);
				return due.isValid() && !due.isBefore(moment.utc(range.start)) && !due.isAfter(moment.utc(range.end));
			}

			/**
			 * Puts the task in a list or takes it out, according to whether it belongs there now.
			 *
			 * @return whether the list actually changed
			 */
			function reconcile(list, task, belongs) {
				const index = indexOf(list, task.task_id);

				if (!belongs) {
					return remove(list, task.task_id);
				}
				if (index === -1) {
					list.push(task);
					return true;
				}
				if (angular.equals(list[index], task)) {
					return false;
				}
				list[index] = task;
				return true;
			}

			/**
			 * Re-renders the view. The callers run inside an $http success handler, so anything this
			 * throws is swallowed into a rejected promise - the screen silently keeps the old data with
			 * nothing in the console to say why. Surface the message only, never the task, which carries
			 * patient details.
			 */
			function render() {
				try {
					spec.onChanged();
				} catch (e) {
					console.error("Live task update could not be applied to the view: " + (e && e.message));
				}
			}

			/** Applies a fetched task to both lists. */
			function patch(task) {
				const assigned = listOf(spec.assignedTasks);
				const practice = listOf(spec.practiceTasks);
				const inWindow = isInWindow(task);
				let changed = false;

				// Neither list has loaded yet, so there is nothing to patch into. Worth saying out loud:
				// it is otherwise indistinguishable from an update that worked and had nothing to do.
				if (!assigned && !practice) {
					console.error("Live task update skipped: this view has no task list loaded yet.");
					return;
				}

				if (assigned) {
					const isMine = LiveReloadService.sameId(task.assigned_to_user_id, Session.userId);
					changed = reconcile(assigned, task, inWindow && isMine) || changed;
				}
				if (practice) {
					// The practice query is not filtered by user, so being in the window is the whole test.
					changed = reconcile(practice, task, inWindow) || changed;
				}

				if (changed) {
					render();
					return;
				}

				// The fetch succeeded and the patch decided nothing needed doing. That is a legitimate
				// outcome - the task was already identical - but it is also what a wrong membership test
				// looks like, and the two are indistinguishable from the outside. Report the decision so
				// they can be told apart. Booleans only; the task itself carries patient details.
				console.error(
					"Live task update changed nothing" +
						" [inWindow=" + inWindow +
						", assignedListLoaded=" + !!assigned +
						", practiceListLoaded=" + !!practice + "]"
				);
			}

			function onUpsert(payload) {
				if (!payload.taskId || !payload.practiceId || !spec.isRelevant(payload)) {
					return;
				}
				if (fetching[payload.taskId]) {
					return;
				}
				fetching[payload.taskId] = true;

				// Through LiveUpdateGuard, which stops this after a few failures in a row.
				LiveUpdateGuard.run("task", function () {
					return TasksAPI.getTask(payload.practiceId, payload.taskId);
				}).then(
					function (resp) {
						delete fetching[payload.taskId];

						const task = resp.data;
						if (task && task.task_id) {
							patch(task);
						}
					},
					function () {
						// Deliberately silent: the poll will not re-announce this change, so the lists stay
						// as they are until the next one. Nothing is logged - the response carries patient data.
						delete fetching[payload.taskId];
					}
				);
			}

			/** A delete needs no request - the id alone says what to drop. */
			function onDelete(payload) {
				if (!payload.taskId) {
					return;
				}

				const assigned = listOf(spec.assignedTasks);
				const practice = listOf(spec.practiceTasks);
				let changed = false;

				if (assigned) {
					changed = remove(assigned, payload.taskId) || changed;
				}
				if (practice) {
					changed = remove(practice, payload.taskId) || changed;
				}

				if (changed) {
					render();
				}
			}

			[PUSH_EVENTS.TASK_ADDED, PUSH_EVENTS.TASK_UPDATED].forEach(function (eventName) {
				scope.$on(eventName, function (e, pushEvent) {
					onUpsert((pushEvent && pushEvent.data) || {});
				});
			});
			scope.$on(PUSH_EVENTS.TASK_DELETED, function (e, pushEvent) {
				onDelete((pushEvent && pushEvent.data) || {});
			});
		}

		const service = {
			subscribe: subscribe,
		};

		return service;
	},
]);
